import { gsap, ScrollTrigger } from "../gsap.js";

function splitToWords(el) {
  const original = el.innerHTML;
  const words = [];

  function processNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text.trim()) return;
      const frag = document.createDocumentFragment();
      // Split keeping whitespace between words
      text.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
        } else {
          const span = document.createElement("span");
          span.className = "ct-word";
          span.style.opacity = "0";
          span.style.display = "inline-block";
          span.textContent = part;
          frag.appendChild(span);
          words.push(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Hide inline text-decoration until animation reveals the text
      if (node.style && node.style.textDecoration) {
        node.dataset.origDecoration = node.style.textDecoration;
        node.style.textDecoration = "none";
      }
      Array.from(node.childNodes).forEach(processNode);
    }
  }

  Array.from(el.childNodes).forEach(processNode);
  return { words, original };
}

export default function initContentTile() {
  const tiles = document.querySelectorAll(".content-tile");
  if (!tiles.length) return;

  tiles.forEach((tile) => {
    const textEl = tile.querySelector(".content-tile-text");
    const imageEl = tile.querySelector(".content-tile-image");
    if (!textEl && !imageEl) return;

    const wordGroups = [];
    const originals = [];
    const btnElements = [];

    if (textEl) {
      Array.from(textEl.children).forEach((child) => {
        if (
          child.classList.contains("btn") ||
          child.classList.contains("btn-link") ||
          child.classList.contains("btn-list") ||
          child.querySelector(".btn, .btn-link")
        ) {
          btnElements.push(child);
        } else {
          const { words, original } = splitToWords(child);
          wordGroups.push(words);
          originals.push({ el: child, html: original });
        }
      });

      gsap.set(btnElements, { autoAlpha: 0, y: -20 });
    }

    let imgEl = null;
    if (imageEl) {
      imgEl = imageEl.querySelector("img");
    }

    // --- ScrollTrigger: word reveal + btn fade in ---
    if (textEl && (wordGroups.length || btnElements.length)) {
      ScrollTrigger.create({
        trigger: tile,
        start: "top 80%",
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({
            onComplete: () => {
              textEl
                .querySelectorAll("[data-orig-decoration]")
                .forEach((n) => {
                  n.style.textDecoration = n.dataset.origDecoration;
                  delete n.dataset.origDecoration;
                });
            },
          });
          wordGroups.forEach((words) => {
            tl.to(
              words,
              {
                opacity: 1,
                duration: 0.15,
                stagger: { each: 0.03, ease: "none" },
                ease: "power1.out",
              },
              tl.duration() > 0 ? "-=0.2" : 0
            );
          });
          if (btnElements.length) {
            tl.to(
              btnElements,
              {
                autoAlpha: 1,
                y: 0,
                duration: 0.5,
                stagger: 0.1,
                ease: "power2.out",
              },
              tl.duration() > 0.3 ? "-=0.3" : 0
            );
          }
        },
      });
    }

    // --- Parallax: text moves up, image moves down ---
    const MD = 744;

    if (textEl) {
      gsap.matchMedia().add("(min-width: " + MD + "px)", () => {
        gsap.fromTo(
          textEl,
          { y: 120 },
          {
            y: -120,
            ease: "none",
            scrollTrigger: {
              trigger: tile,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      });
    }

    if (imgEl) {
      gsap.fromTo(
        imgEl,
        {
          scale: () => (window.innerWidth >= MD ? 1.4 : 1.15),
          y: () => (window.innerWidth >= MD ? -150 : -50),
        },
        {
          y: () => (window.innerWidth >= MD ? 150 : 50),
          ease: "none",
          scrollTrigger: {
            trigger: imageEl,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
    }
  });
}
